<template>
  <div
    v-if="shouldShowReview"
    class="flex p-2.5 mt-4 lg:mb-0 mb-9 border border-EXPONDO-grey-10 rounded"
  >
    <div>
      <ReviewsStars
        class="mt-1 lg:mt-0 mb-2.5 "
        :rating="rating"
        :show-schema="false"
      />
      <ReviewAuthor
        :author="author"
        :time-ago="timeAgo"
        :is-top-review="true"
        :show-schema="false"
      />
    </div>
    <div class="pl-2.5 text-sm">
      <strong
        class="mb-2.5 leading-5"
      >{{ title }}</strong>
      <p
        class="line-clamp-3 mb-2.5 leading-5"
        :class="{'italic': isReviewEmpty}"
      >
        {{ message }}
      </p>
      <button
        class="text-EXPONDO-secondary underline"
        @click="scrollToReviews"
      >
        {{ $t('Read more') }}
      </button>
    </div>
  </div>
</template>
<script lang="ts">

import type { ProductReview } from '~~/types/product/attribute/ProductReview';

import { reviewGetters } from '~/utils/ct';
import { getTimeAgo } from '~~/helpers/time/getTimeAgo';
import { PDP_NAV_ANCHORS } from '~~/constants/pdpNavAnchors';
import { getTranslatedCustomField } from '~~/helpers/review/getTranslatedCustomField/getTranslatedCustomField';
import { scrollToSection } from '~~/helpers/scroll/scrollToSection';

export default defineComponent({
  name: 'ProductTopReview',

  props: {
    review: {
      type: Object as PropType<ProductReview>,
      default: () => {

        return {};
      
      },
    },
  },
  setup(props) {

    const { review } = toRefs(props);
    const { languageAndCountry: activeLocale } = useLocalization();
    const relativeTime = computed(() =>
      new Intl.RelativeTimeFormat(activeLocale.value, { style: 'long', numeric: 'auto' }));

    const date = computed(() => review.value.custom?.reviewCreatedAt?.[0]?.value || review.value.createdAt);
    const time = computed(() => getTimeAgo(date.value ? new Date(date.value) : undefined).time);
    const unit = computed(() => getTimeAgo(date.value ? new Date(date.value) : undefined).unit);
    const timeAgo = computed(() => time.value && unit.value ? relativeTime.value?.format(time.value, unit.value) : '');
    const translatedTitle = computed(() =>
      getTranslatedCustomField({ review: review.value, field: 'titleTranslations', activeLocale: activeLocale.value }));
    const translatedMessage = computed(() =>
      getTranslatedCustomField({ review: review.value, field: 'textTranslations', activeLocale: activeLocale.value }));
    const originalMessage = computed(() => reviewGetters.getReviewMessage(review.value));
    const title = computed(() => translatedTitle.value || review.value?.title);
    const message = computed(() => translatedMessage.value || originalMessage.value);
    const isReviewEmpty = computed(() => !originalMessage.value);

    const rating = computed(() => reviewGetters.getReviewRating(review.value));
    const author = computed(() => reviewGetters.getReviewAuthor(review.value));
    const shouldShowReview = computed(() => review.value && rating.value >= 4);

    const { stickyAnchorNavHeight } = useAnchorNavigationStore();
    const scrollToReviews = () => {

      scrollToSection(`#${PDP_NAV_ANCHORS.RATING_REVIEWS}`, -stickyAnchorNavHeight.value);
    
    };

    return {
      rating,
      author,
      title,
      message,
      timeAgo,
      isReviewEmpty,
      scrollToReviews,
      shouldShowReview,
    };
  
  },
});

</script>
